import Box from '@mui/material/Box'
import { observer } from 'mobx-react-lite'
import { motion } from "framer-motion"
import ModalState from 'reusable/mobx-helpers/ModalState'
import useKeyInputListener from 'reusable/hooks/useKeyInputListener'

interface ClippyProps {
   text: React.ReactNode
   modalState: ModalState
}

export default observer(function Clippy(props: ClippyProps) {
   // Close clippy if escape key is detected
   useKeyInputListener(() => {
      if (props.modalState.visible) {
         props.modalState.close()
      }
   }, 'Escape')

   if (!props.modalState.visible) {
      return null
   }

   const handleOverlayClick = (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) {
         props.modalState.close()
      }
   }

   const stopPropagation: React.MouseEventHandler<HTMLDivElement> | undefined = event => event.stopPropagation()

   return <Box
      sx={{
         position: "fixed",
         bottom: 0,
         right: 0,
         zIndex: 1300,
         width: "100vw",
         height: "100vh",
         backgroundColor: "rgba(0, 0, 0, 0.5)",
         display: "flex",
         justifyContent: "flex-end",
         alignItems: "flex-end",
         pointerEvents: props.modalState.visible ? "auto" : "none",
      }}
      onClick={handleOverlayClick}
   >
      <motion.div
         initial={{ x: "100%" }}
         animate={{ x: props.modalState.visible ? 0 : "100%" }}
         transition={{ type: "spring", stiffness: 75, damping: 5 }}
         onClick={stopPropagation}
      >
         <Box
            sx={{
               position: "relative",
               margin: "2vmin",
               pointerEvents: "auto",
            }}
         >
            <Box
               sx={{
                  width: "30vmin",
                  height: "24vmin",
                  background: "#f8f8d0",
                  backgroundImage:
                     "linear-gradient(to right, transparent 15%, #f64 0 16%, transparent 0), repeating-linear-gradient(transparent 0 9%, #6ba 0 9.25%)",
                  position: "absolute",
                  transform: "rotateY(25deg) rotateX(75deg) rotateZ(-30deg)",
                  bottom: "2vmin",
                  left: "-46vmin",
               }}
            />
            <Box
               sx={{
                  position: "absolute",
                  bottom: "10vmin",
                  left: "-36vmin",
                  width: "9vmin",
                  height: "12vmin",
                  border: "1vmin solid #aaa",
                  borderTop: 0,
                  borderRadius: "0 0 10vmin 10vmin",
                  boxSizing: "border-box",
                  "&::before": {
                     content: "''",
                     display: "block",
                     position: "absolute",
                     top: "-15vmin",
                     left: "-1vmin",
                     width: "7.75vmin",
                     height: "15vmin",
                     border: "1vmin solid #aaa",
                     borderBottom: 0,
                     borderRadius: "10vmin 10vmin 0 0",
                     boxSizing: "border-box",
                  },
                  "&::after": {
                     content: "''",
                     display: "block",
                     position: "absolute",
                     top: "-2.5vmin",
                     left: "0.25vmin",
                     width: "6.5vmin",
                     height: "9vmin",
                     border: "1vmin solid #aaa",
                     borderTop: 0,
                     borderRadius: "0 0 10vmin 10vmin",
                     boxSizing: "border-box",
                  },
               }}
            >
               <Box
                  sx={{
                     position: "absolute",
                     top: "-75%",
                     left: "-35%",
                     width: "75%",
                     height: "35%",
                     background: "white",
                     borderRadius: "50%",
                     zIndex: 1,
                     backgroundImage: "radial-gradient(circle, black 15%, transparent 0)",
                     boxShadow:
                        "inset 0 1.25vmin 0 -0.5vmin #aaa, inset 0 0 1vmin #aaa",
                     "&::before": {
                        content: "''",
                        display: "block",
                        position: "absolute",
                        top: "-20%",
                        width: "100%",
                        height: "90%",
                        borderRadius: "50%",
                        boxShadow: "-0.25vmin -1vmin 0 -0.25vmin black",
                     },
                  }}
               />
               <Box
                  sx={{
                     position: "absolute",
                     top: "-75%",
                     left: "10%",
                     width: "75%",
                     height: "35%",
                     background: "white",
                     borderRadius: "50%",
                     zIndex: 1,
                     backgroundImage: "radial-gradient(circle, black 15%, transparent 0)",
                     boxShadow:
                        "inset 0 1.25vmin 0 -0.5vmin #aaa, inset 0 0 1vmin #aaa",
                     "&::before": {
                        content: "''",
                        display: "block",
                        position: "absolute",
                        top: "-20%",
                        width: "100%",
                        height: "90%",
                        borderRadius: "50%",
                        boxShadow: "0.25vmin -1vmin 0 -0.25vmin black",
                     },
                  }}
               />
            </Box>
            <Box
               sx={{
                  position: "absolute",
                  bottom: "35vmin",
                  left: "-30vmin",
                  width: "30vmin",
                  padding: "2vmin",
                  background: "#ffc",
                  borderRadius: "1vmin",
                  border: "0.375vmin solid #666",
                  fontSize: "3vmin",
                  fontFamily: "Tahoma, 'Courier New', Courier, sans-serif",
                  lineHeight: 1.25,
                  maxHeight: '400px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  "&::before": {
                     content: "''",
                     display: "block",
                     position: "absolute",
                     top: "100%",
                     left: "10%",
                     width: "6vmin",
                     height: "6vmin",
                     borderLeft: "0.375vmin solid #666",
                     backgroundImage:
                        "linear-gradient(-45deg, transparent 50%, #666 0 54%, #ffc 0)",
                     transform: "skew(-30deg)",
                  },
               }}
            >
               {props.text}
            </Box>
         </Box>
      </motion.div>
   </Box>
})
